/**
 * Everspring PDP – Controller
 * Handles upsell selection, total price card, scroll indicator, and cart submission.
 */
(function () {
  'use strict';

  /**
   * Format cents to Euro currency string
   * @param {number} cents
   * @returns {string}
   */
  function formatMoney(cents) {
    var euros = (cents / 100).toFixed(2).replace('.', ',');
    return '\u20AC' + euros;
  }

  /**
   * Parse a Euro price string to cents
   * @param {string} text
   * @returns {number}
   */
  function parsePriceToCents(text) {
    if (!text) return 0;
    var cleaned = text.replace(/[^0-9,.\-]/g, '').replace(',', '.');
    return Math.round(parseFloat(cleaned) * 100) || 0;
  }

  /**
   * Dutch day and month names for formatting delivery dates
   */
  var DUTCH_DAYS = ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'];
  var DUTCH_MONTHS = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];

  /**
   * Calculate the next available delivery dates.
   * @param {Object} opts
   * @param {number} opts.cutoffDay    Day of week for order cutoff (0=Sun..6=Sat)
   * @param {number} opts.cutoffHour   Hour (0-23) for order cutoff
   * @param {number} opts.deliveryDay  Day of week for delivery (0=Sun..6=Sat)
   * @param {number} opts.count        Number of dates to generate
   * @returns {Date[]}
   */
  function getDeliveryDates(opts) {
    var now = new Date();
    var cutoffDay = opts.cutoffDay;
    var cutoffHour = opts.cutoffHour;
    var deliveryDay = opts.deliveryDay;
    var count = opts.count;

    // Find the next delivery day from today
    var candidate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    var dow = candidate.getDay();
    var daysUntilDelivery = (deliveryDay - dow + 7) % 7;
    if (daysUntilDelivery === 0) daysUntilDelivery = 7; // If today is delivery day, next week
    candidate.setDate(candidate.getDate() + daysUntilDelivery);

    // Calculate the cutoff moment for this candidate delivery date
    // The cutoff is on cutoffDay at cutoffHour, in the same week as the delivery
    // (or the week before if cutoffDay > deliveryDay)
    function getCutoffForDelivery(deliveryDate) {
      var cutoff = new Date(deliveryDate);
      var dayDiff = (deliveryDay - cutoffDay + 7) % 7;
      cutoff.setDate(cutoff.getDate() - dayDiff);
      cutoff.setHours(cutoffHour, 0, 0, 0);
      return cutoff;
    }

    // If the cutoff for the first candidate has passed, skip to next week
    if (now >= getCutoffForDelivery(candidate)) {
      candidate.setDate(candidate.getDate() + 7);
    }

    var dates = [];
    for (var i = 0; i < count; i++) {
      dates.push(new Date(candidate));
      candidate.setDate(candidate.getDate() + 7);
    }
    return dates;
  }

  /**
   * Format a date as a Dutch readable string, e.g. "Vrijdag 28 februari"
   * @param {Date} date
   * @returns {string}
   */
  function formatDeliveryDate(date) {
    var dayName = DUTCH_DAYS[date.getDay()];
    return dayName.charAt(0).toUpperCase() + dayName.slice(1) + ' ' + date.getDate() + ' ' + DUTCH_MONTHS[date.getMonth()];
  }

  /**
   * Populate delivery date select elements
   */
  function initDeliveryDatePickers() {
    var selects = document.querySelectorAll('[data-delivery-date-select]');
    selects.forEach(function (select) {
      var cutoffDay = parseInt(select.dataset.cutoffDay, 10) || 1;
      var cutoffHour = parseInt(select.dataset.cutoffHour, 10) || 17;
      var deliveryDay = parseInt(select.dataset.deliveryDay, 10) || 5;
      var weeksAhead = parseInt(select.dataset.weeksAhead, 10) || 8;

      var dates = getDeliveryDates({
        cutoffDay: cutoffDay,
        cutoffHour: cutoffHour,
        deliveryDay: deliveryDay,
        count: weeksAhead,
      });

      // Clear placeholder
      select.innerHTML = '';

      dates.forEach(function (date, index) {
        var option = document.createElement('option');
        option.value = formatDeliveryDate(date);
        option.textContent = formatDeliveryDate(date);
        if (index === 0) option.selected = true;
        select.appendChild(option);
      });
    });
  }

  class PdpController {
    constructor(sectionEl) {
      this.section = sectionEl;
      this.sectionId = sectionEl.dataset.section;

      // Upsell elements
      this.upsellContainer = sectionEl.querySelector('[id^="Upsell-"]');
      this.upsellScrollContainer = sectionEl.querySelector('[data-upsell-scroll]');
      this.upsellCards = sectionEl.querySelectorAll('[data-upsell-card]');
      // Total price card elements
      this.totalCard = sectionEl.querySelector('[id^="TotalCard-"]');
      this.totalToggle = sectionEl.querySelector('[data-total-toggle]');
      this.totalBreakdown = sectionEl.querySelector('[data-total-breakdown]');
      this.totalPriceEl = sectionEl.querySelector('[data-total-price]');
      this.totalCompareEl = sectionEl.querySelector('[data-total-compare]');
      this.productPriceEl = sectionEl.querySelector('[data-product-price]');
      this.upsellBreakdownRow = sectionEl.querySelector('[data-upsell-breakdown-row]');
      this.upsellBreakdownName = sectionEl.querySelector('[data-upsell-breakdown-name]');
      this.upsellBreakdownPrice = sectionEl.querySelector('[data-upsell-breakdown-price]');

      // Scroll indicator
      this.scrollIndicator = sectionEl.querySelector('[data-scroll-indicator]');
      this.scrollThumb = sectionEl.querySelector('[data-scroll-thumb]');

      // State
      this.selectedUpsellVariantId = null;
      this.selectedUpsellPrice = 0;
      this.selectedUpsellTitle = '';

      this.bindEvents();
      this.initScrollIndicator();
      this.observePriceChanges();
      this.updateTotalPrice();
    }

    bindEvents() {
      var self = this;

      // Upsell card toggle
      this.upsellCards.forEach(function (card) {
        card.addEventListener('click', function () {
          if (card.classList.contains('es-pdp__upsell-thumb--active')) {
            self.deselectUpsell();
          } else {
            self.selectUpsell(card);
          }
        });
      });

      // Total price breakdown toggle
      if (this.totalToggle) {
        this.totalToggle.addEventListener('click', function () {
          self.toggleBreakdown();
        });
      }
    }

    /**
     * Select an upsell product
     */
    selectUpsell(card) {
      // Remove active from all
      this.upsellCards.forEach(function (c) {
        c.classList.remove('es-pdp__upsell-thumb--active');
      });

      // Activate clicked card
      card.classList.add('es-pdp__upsell-thumb--active');
      this.selectedUpsellVariantId = card.dataset.upsellVariantId;
      this.selectedUpsellPrice = parseInt(card.dataset.upsellPrice, 10) || 0;
      this.selectedUpsellTitle = card.dataset.upsellTitle || '';

      this.updateTotalPrice();
    }

    /**
     * Deselect upsell
     */
    deselectUpsell() {
      this.upsellCards.forEach(function (c) {
        c.classList.remove('es-pdp__upsell-thumb--active');
      });

      this.selectedUpsellVariantId = null;
      this.selectedUpsellPrice = 0;
      this.selectedUpsellTitle = '';

      this.updateTotalPrice();
    }

    /**
     * Toggle total price breakdown visibility
     */
    toggleBreakdown() {
      if (!this.totalBreakdown) return;
      var isExpanded = !this.totalBreakdown.hidden;
      this.totalBreakdown.hidden = !this.totalBreakdown.hidden;

      var chevron = this.section.querySelector('.es-pdp__total-chevron');
      if (chevron) {
        chevron.setAttribute('aria-expanded', String(!isExpanded));
      }
    }

    /**
     * Get current product price from the DOM
     */
    getProductPrice() {
      var priceSection = document.getElementById('price-' + this.sectionId);
      if (!priceSection) return 0;

      // Try sale price first, then regular
      var priceEl =
        priceSection.querySelector('.price__sale .price-item--sale') ||
        priceSection.querySelector('.price-item--regular');
      if (!priceEl) return 0;

      return parsePriceToCents(priceEl.textContent);
    }

    /**
     * Get compare-at price from the DOM
     */
    getCompareAtPrice() {
      var priceSection = document.getElementById('price-' + this.sectionId);
      if (!priceSection) return 0;

      var compareEl = priceSection.querySelector('.price__sale s.price-item--regular');
      if (!compareEl) return 0;

      return parsePriceToCents(compareEl.textContent);
    }

    /**
     * Recalculate and update total price display
     */
    updateTotalPrice() {
      var productPrice = this.getProductPrice();
      var total = productPrice + this.selectedUpsellPrice;

      // Promo discount: data-promo-discount="25" on the total card
      var promoDiscount = this.totalCard && this.totalCard.dataset.promoDiscount
        ? parseInt(this.totalCard.dataset.promoDiscount, 10) : 0;

      if (promoDiscount > 0) {
        var originalTotal = total;
        var discountedTotal = Math.round(total * (100 - promoDiscount) / 100);

        if (this.totalPriceEl) {
          this.totalPriceEl.textContent = formatMoney(discountedTotal);
          this.totalPriceEl.classList.add('es-pdp__total-amount--sale');
        }
        if (this.totalCompareEl) {
          this.totalCompareEl.textContent = formatMoney(originalTotal);
          this.totalCompareEl.style.display = '';
        }
      } else {
        // Update total amount
        if (this.totalPriceEl) {
          this.totalPriceEl.textContent = formatMoney(total);
          this.totalPriceEl.classList.remove('es-pdp__total-amount--sale');
        }

        // Update compare-at in total
        var compareAt = this.getCompareAtPrice();
        if (this.totalCompareEl) {
          if (compareAt > 0) {
            var compareTotal = compareAt + this.selectedUpsellPrice;
            this.totalCompareEl.textContent = formatMoney(compareTotal);
            this.totalCompareEl.style.display = '';
          } else {
            this.totalCompareEl.style.display = 'none';
          }
        }
      }

      // Update breakdown rows
      if (this.productPriceEl) {
        this.productPriceEl.textContent = formatMoney(productPrice);
      }
      if (this.upsellBreakdownRow) {
        this.upsellBreakdownRow.style.display = this.selectedUpsellVariantId ? '' : 'none';
        if (this.upsellBreakdownName) this.upsellBreakdownName.textContent = this.selectedUpsellTitle;
        if (this.upsellBreakdownPrice) this.upsellBreakdownPrice.textContent = formatMoney(this.selectedUpsellPrice);
      }
    }

    /**
     * Observe price element for changes (variant switching)
     */
    observePriceChanges() {
      var priceSection = document.getElementById('price-' + this.sectionId);
      if (!priceSection) return;

      var observer = new MutationObserver(this.updateTotalPrice.bind(this));
      observer.observe(priceSection, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    /**
     * Initialize the scroll indicator for the upsell carousel
     */
    initScrollIndicator() {
      if (!this.upsellScrollContainer || !this.scrollIndicator || !this.scrollThumb) return;

      var self = this;
      var updateThumb = function () {
        var el = self.upsellScrollContainer;
        var scrollLeft = el.scrollLeft;
        var scrollWidth = el.scrollWidth;
        var clientWidth = el.clientWidth;

        if (scrollWidth <= clientWidth) {
          self.scrollIndicator.style.display = 'none';
          return;
        }
        self.scrollIndicator.style.display = '';
        var ratio = clientWidth / scrollWidth;
        var maxScroll = scrollWidth - clientWidth;
        var position = maxScroll > 0 ? scrollLeft / maxScroll : 0;
        var thumbWidth = Math.max(ratio * 100, 15);
        var maxLeft = 100 - thumbWidth;

        self.scrollThumb.style.width = thumbWidth + '%';
        self.scrollThumb.style.left = position * maxLeft + '%';
      };

      this.upsellScrollContainer.addEventListener('scroll', updateThumb, { passive: true });
      window.addEventListener('resize', updateThumb);
      requestAnimationFrame(updateThumb);
    }

    // Public API
    getSelectedUpsellVariantId() {
      return this.selectedUpsellVariantId;
    }
  }

  /**
   * Initialize controllers and cart interception
   */
  function init() {
    var sections = document.querySelectorAll('product-info.es-pdp-section');
    var instanceMap = new Map();

    sections.forEach(function (section) {
      var controller = new PdpController(section);
      instanceMap.set(section, controller);
    });

    if (instanceMap.size === 0) return;

    // Intercept form submission for multi-item cart add
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('product-form form, form[action*="/cart/add"]');
      if (!form) return;

      // Check if this form belongs to our section
      var productInfo = form.closest('product-info.es-pdp-section');
      if (!productInfo) return;

      var instance = instanceMap.get(productInfo);
      if (!instance) return;

      // Prevent double-add: if already added, open cart drawer instead
      var submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn && submitBtn.hasAttribute('data-added')) {
        e.preventDefault();
        e.stopPropagation();
        var cartDrawerEl = document.querySelector('cart-drawer');
        if (cartDrawerEl && typeof cartDrawerEl.open === 'function') {
          cartDrawerEl.open();
        } else {
          window.location.href = '/cart';
        }
        return;
      }

      var upsellVariantId = instance.getSelectedUpsellVariantId();

      if (upsellVariantId) {
        e.preventDefault();
        e.stopPropagation();

        var formData = new FormData(form);

        // Collect line item properties from the form
        var properties = {};
        for (var pair of formData.entries()) {
          var match = pair[0].match(/^properties\[(.+)\]$/);
          if (match && pair[1]) {
            properties[match[1]] = pair[1];
          }
        }

        var mainItem = {
          id: parseInt(formData.get('id'), 10),
          quantity: parseInt(formData.get('quantity'), 10) || 1,
        };
        if (Object.keys(properties).length > 0) {
          mainItem.properties = properties;
        }

        var items = [
          mainItem,
          {
            id: parseInt(upsellVariantId, 10),
            quantity: 1,
          },
        ];

        // Disable button during submission
        var submitBtn = form.querySelector('[type="submit"]');
        if (submitBtn) {
          submitBtn.setAttribute('disabled', 'disabled');
          submitBtn.classList.add('loading');
        }

        // Use Dawn's product-form pattern for cart updates
        var cartDrawer = document.querySelector('cart-drawer');
        var sectionsToFetch = [];
        if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
          // Use .section (Shopify section id) — NOT .id (DOM element id)
          sectionsToFetch = cartDrawer.getSectionsToRender().map(function (s) { return s.section || s.id; });
        }

        var addBody = { items: items };
        if (sectionsToFetch.length) {
          addBody.sections = sectionsToFetch;
          addBody.sections_url = window.location.pathname;
        }

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(addBody),
        })
          .then(function (response) {
            if (!response.ok) throw new Error('Cart add failed');
            return response.json();
          })
          .then(function (parsedState) {
            // Server returned an error (e.g. out of stock)
            if (parsedState.status) {
              console.error('[es-pdp] cart/add error:', parsedState);
              return;
            }

            // PostHog: track add_to_cart event
            if (window.posthog && typeof window.posthog.capture === 'function') {
              try {
                var addedItems = parsedState.items || [];
                addedItems.forEach(function (item) {
                  window.posthog.capture('add_to_cart', {
                    product_id: item.product_id,
                    product_title: item.product_title || item.title,
                    variant_id: item.variant_id || item.id,
                    variant_title: item.variant_title,
                    price: (item.price || 0) / 100,
                    quantity: item.quantity,
                    line_price: (item.line_price || 0) / 100,
                    currency: (parsedState.currency || 'EUR'),
                    cart_total: (parsedState.total_price || 0) / 100,
                    cart_item_count: parsedState.item_count,
                    source: 'es-pdp'
                  });
                });
              } catch (e) { console.warn('[posthog] add_to_cart capture failed', e); }
            }

            // Dawn pattern: publish cartUpdate first, then renderContents
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'es-pdp',
                productVariantId: items[0] ? items[0].id : null,
                cartData: parsedState,
              });
            }

            if (cartDrawer && parsedState.sections && typeof cartDrawer.renderContents === 'function') {
              cartDrawer.renderContents(parsedState);
            }

            // Update cart count badges by reading the live bubble after render
            var bubbleCount = document.querySelector('.cart-count-bubble span[aria-hidden="true"]');
            if (bubbleCount) {
              var badges = document.querySelectorAll('[data-cart-count], #es-cart-count');
              badges.forEach(function (el) {
                el.textContent = bubbleCount.textContent;
                var parent = el.closest('[hidden]');
                if (parent) parent.removeAttribute('hidden');
              });
            }

            // Prevent double-add: change button to "added" state
            if (submitBtn) {
              submitBtn.classList.remove('loading');
              submitBtn.setAttribute('data-added', 'true');
              var btnText = submitBtn.querySelector('.es-pdp__atc-btn-text');
              if (btnText) btnText.textContent = '✓ Toegevoegd';
              submitBtn.removeAttribute('disabled');
            }
          })
          .catch(function (err) {
            console.error('[es-pdp] cart/add failed:', err);
            form.submit();
          })
          .finally(function () {
            if (submitBtn && !submitBtn.hasAttribute('data-added')) {
              submitBtn.removeAttribute('disabled');
              submitBtn.classList.remove('loading');
            }
          });
      }
      // If no upsell selected, let the default product-form.js handle submission
    });
  }

  /**
   * Favorite button — integrates with the esWishlist localStorage system
   * used by es-header.liquid and es-product-card.js
   */
  function initFavorites() {
    var STORAGE_KEY = 'esWishlist';

    function getWishlist() {
      try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
      } catch (e) {
        return [];
      }
    }

    function saveWishlist(list) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
      } catch (e) {}
    }

    function findIndex(wishlist, productId) {
      for (var i = 0; i < wishlist.length; i++) {
        var item = wishlist[i];
        var itemId = typeof item === 'object' ? item.id : item;
        if (String(itemId) === String(productId)) return i;
      }
      return -1;
    }

    document.querySelectorAll('.es-pdp__favorite-btn').forEach(function (btn) {
      var productId = btn.dataset.productId;
      var handle = btn.dataset.productHandle || '';
      var addLabel = btn.dataset.tooltipAdd || 'Add to wishlist';
      var removeLabel = btn.dataset.tooltipRemove || 'Remove from wishlist';
      var tooltip = btn.querySelector('.es-pdp__favorite-tooltip');
      if (!productId) return;

      function updateLabels(isFavorited) {
        var label = isFavorited ? removeLabel : addLabel;
        btn.setAttribute('aria-label', label);
        if (tooltip) tooltip.textContent = label;
      }

      // Set initial state
      if (findIndex(getWishlist(), productId) !== -1) {
        btn.classList.add('is-favorited');
        updateLabels(true);
      }

      btn.addEventListener('click', function () {
        var wishlist = getWishlist();
        var idx = findIndex(wishlist, productId);

        if (idx === -1) {
          wishlist.push({ id: productId, handle: handle });
          btn.classList.add('is-favorited');
          updateLabels(true);
        } else {
          wishlist.splice(idx, 1);
          btn.classList.remove('is-favorited');
          updateLabels(false);
        }

        saveWishlist(wishlist);
        document.dispatchEvent(new CustomEvent('wishlist:updated'));
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
      initFavorites();
      initDeliveryDatePickers();
    });
  } else {
    init();
    initFavorites();
    initDeliveryDatePickers();
  }
})();
